import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import ConsultorDashboard from '../components/ConsultorDashboard';
import SalesFunnel from '../components/SalesFunnel';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import { PRICING } from '../constants';
import { leadsService } from '../services/api';

const Analytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'funnel' | 'consultors' | 'whatsapp'>('overview');
  const [overviewStats, setOverviewStats] = useState({
    totalLeads: 0,
    vendasConvertidas: 0,
    taxaConversao: 0,
    consultoresAtivos: 0,
    receitaTotal: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOverviewStats = async () => {
      try {
        setLoading(true);
        const response = await leadsService.getLeadStats({ period: '30d' });
        
        console.log('Resposta da API Stats:', response);
        
        setOverviewStats({
          totalLeads: Number(response.total_leads || 0),
          vendasConvertidas: Number(response.vendas_convertidas || 0),
          taxaConversao: Number(response.taxa_conversao || 0),
          consultoresAtivos: Number(response.consultores_ativos || 0),
          receitaTotal: Number(response.vendas_convertidas || 0) * PRICING.PRODUTO_PRECO
        });
      } catch (error) {
        console.error('Erro ao carregar estatísticas gerais:', error);
        console.log('Detalhes do erro:', error);
        
        // Tentar sem filtro de período
        try {
          const fallbackResponse = await leadsService.getLeadStats({});
          console.log('Resposta fallback Stats:', fallbackResponse);
          
          setOverviewStats({
            totalLeads: Number(fallbackResponse.total_leads || 0),
            vendasConvertidas: Number(fallbackResponse.vendas_convertidas || 0),
            taxaConversao: Number(fallbackResponse.taxa_conversao || 0),
            consultoresAtivos: Number(fallbackResponse.consultores_ativos || 0),
            receitaTotal: Number(fallbackResponse.vendas_convertidas || 0) * PRICING.PRODUTO_PRECO
          });
        } catch (fallbackError) {
          console.error('Erro no fallback Stats também:', fallbackError);
          
          // Usar dados mock apenas como último recurso
          setOverviewStats({
            totalLeads: 0,
            vendasConvertidas: 0,
            taxaConversao: 0,
            consultoresAtivos: 0,
            receitaTotal: 0
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === 'overview') {
      loadOverviewStats();
    }
  }, [activeTab]);

  const tabs = [
    { key: 'overview', label: '📊 Visão Geral', icon: '📊' },
    { key: 'funnel', label: '🎯 Funil de Vendas', icon: '🎯' },
    { key: 'consultors', label: '👥 Performance Consultores', icon: '👥' },
    { key: 'whatsapp', label: '📱 WhatsApp Analytics', icon: '📱' }
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">📈 Analytics Avançado</h1>
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-8">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.key
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Conteúdo das Tabs */}
          <div className="mt-6">
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">📊 Visão Geral do CRM</h2>
                  <p className="text-gray-700 mb-6">
                    Acompanhe os principais indicadores de performance do seu sistema de gestão de leads.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-white p-6 rounded-lg shadow">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 text-sm">🎯</span>
                          </div>
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              Total de Leads
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {loading ? '...' : overviewStats.totalLeads.toLocaleString('pt-BR')}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <span className="text-green-600 text-sm">✅</span>
                          </div>
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              Taxa de Conversão
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {loading ? '...' : `${Number(overviewStats.taxaConversao || 0).toFixed(1)}%`}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                            <span className="text-yellow-600 text-sm">💰</span>
                          </div>
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              Receita Total
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {loading ? '...' : `${PRICING.SIMBOLO} ${overviewStats.receitaTotal.toLocaleString('pt-BR')}`}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-purple-600 text-sm">👥</span>
                          </div>
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              Consultores Ativos
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {loading ? '...' : overviewStats.consultoresAtivos}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">📈 Tendência Mensal</h3>
                    <div className="h-64 flex items-center justify-center text-gray-400">
                      <div className="text-center">
                        <div className="text-4xl mb-2">📊</div>
                        <div>Gráfico de tendências será exibido aqui</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">🗺️ Distribuição por Território</h3>
                    <div className="space-y-3">
                      {[
                        { territory: 'São Paulo - Capital', leads: 485, percentage: 38.9 },
                        { territory: 'São Paulo - Interior', leads: 298, percentage: 23.9 },
                        { territory: 'Rio de Janeiro', leads: 186, percentage: 14.9 },
                        { territory: 'Minas Gerais', leads: 142, percentage: 11.4 },
                        { territory: 'Outros', leads: 136, percentage: 10.9 }
                      ].map((item) => (
                        <div key={item.territory} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                            <span className="text-sm font-medium text-gray-900">{item.territory}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">{item.leads}</span>
                            <span className="text-sm text-gray-400">({item.percentage}%)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'funnel' && <SalesFunnel />}
            
            {activeTab === 'consultors' && <ConsultorDashboard />}
            
            {activeTab === 'whatsapp' && <AnalyticsDashboard />}
          </div>
        </div>
    </Layout>
  );
};

export default Analytics;